import React, { useState, useMemo } from 'react';
import { Plus, X, Edit, Search } from 'lucide-react';
import { useUsers } from '../../hooks/useUsers';
import UserTable from './UserTable';
import AddUserModal from './AddUserModal';
import BulkUploadModal from './BulkUploadModal';
import EditUserModal from './EditUserModal';
import { NewUserInput, User } from '../../types/user';
import { useAdmin } from '../../contexts/adminContext';

interface UserManagementProps {
  darkMode?: boolean;
}

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  React.useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
};

const UserManagement: React.FC<UserManagementProps> = ({ darkMode = false }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://vela-back.wingtrill.com";

  const {
    addUser,
    bulkAddCsv,
    updateUser,
    changePassword,
  } = useUsers();

  const { allUsers, deleteUser } = useAdmin();

  const windowWidth = useWindowWidth();
  const isMobileView = windowWidth < 768;

  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmIds, setConfirmIds] = useState<string[]>([]);
  const [confirmInput, setConfirmInput] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleAdd = async (input: NewUserInput) => {
    await addUser(input);
  };

  const onSubmitBulk = async (csvText: string) => {
    return await bulkAddCsv(csvText);
  };

  const requestDeleteSelected = (ids: string[]) => {
    setConfirmIds(ids);
    setConfirmInput('');
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (confirmInput !== 'DELETE') return;
    setConfirmLoading(true);
    try {
      for (const id of confirmIds) {
        await deleteUser(id);
      }
      setConfirmOpen(false);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
  };

  const handleEditSubmit = async (updates: Partial<User>) => {
    if (!editingUser) return;
    await updateUser(editingUser.id, updates);
  };

  const handleChangePassword = async (newPassword: string) => {
    if (!editingUser) return;
    await changePassword(editingUser.id, newPassword);
  };

  const mappedMobileUsers = useMemo(() => {
    return allUsers.map((u: any) => {
      const nameParts = (u.Name || "").split(" ");
     return {
  id: String(u.id),
  firstName: nameParts[0] || "",
  lastName: nameParts.slice(1).join(" ") || "",
  email: u.Email || "",
  phoneNumber: u.phone_number || "-",
  department: u.Department || "-",
  position: u.job_title || "-",
  location: u.location || "-",
  role: u.isAdmin ? "Admin" : "User",
  createdAt: u.Created_at,
  avatarUrl: u.avatarUrl
    ? backendUrl + u.avatarUrl
    : "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png",
} as User;

    });
  }, [allUsers]);

const renderMobileControls = () => (
  <div className="flex flex-col gap-3">

    {/* TOP ROW: Search + Plus + Delete */}
    <div className="flex items-center gap-3">

      {/* Search Bar */}
      <div className="relative flex-1">
        <input
          type="text"
          placeholder="Search users..."
          className={`w-full rounded-lg px-3 py-2 text-sm pl-10 focus:outline-none focus:ring-2 focus:ring-pink-500 border ${
            darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : 'border-gray-300'
          }`}
        />
        <Search
          size={16}
          className={`absolute left-3 top-1/2 -translate-y-1/2 ${
            darkMode ? 'text-white/60' : 'text-gray-500'
          }`}
        />
      </div>

      {/* ADD USER BUTTON */}
      <button
        onClick={() => setShowAdd(true)}
        className="flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-r from-red-600 to-pink-600 text-white shrink-0"
      >
        <Plus size={18} />
      </button>

      {/* DELETE ICON BUTTON */}
      <button
        onClick={() => requestDeleteSelected([])} 
        disabled={true} 
        className={`flex items-center justify-center h-10 w-10 rounded-xl border shrink-0 ${
          darkMode ? 'border-white/20 text-white/40' : 'border-gray-300 text-gray-500'
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
          viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8
                   a2 2 0 0 1-2-2L5 6m3 0V4
                   a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>

    </div>

    {/* SECOND ROW — Filters + Page Size */}
    <div className="flex items-center gap-3">
      <button
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border ${
          darkMode ? 'bg-white/5 border-white/10 text-white/80' : 'bg-gray-100 border-gray-200 text-gray-700'
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
          viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        Filter
      </button>

      <select
        className={`rounded-lg  py-2.95 text-sm border ${
          darkMode ? 'bg-[#0f1420] border-white/10 text-white' : 'border-gray-300'
        }`}
      >
        {[10, 25, 50].map((size) => (
          <option key={size}>{size} / page</option>
        ))}
      </select>
    </div>

  </div>
);


  const renderUserCards = () => {
    if (mappedMobileUsers.length === 0) {
      return <p className={`text-center py-10 ${darkMode ? 'text-white/70' : 'text-gray-500'}`}>No users found.</p>;
    }
    return (
      <div className="space-y-4 pt-4">
        {mappedMobileUsers.map((user) => (
          <div key={user.id} className={`p-4 rounded-xl border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3 min-w-0 flex-grow">
               {user.avatarUrl ? (
  <img
    src={user.avatarUrl}
    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
  />
) : (
  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-600 to-pink-600 text-white flex items-center justify-center text-xs font-semibold">
    {user.firstName[0]}{user.lastName[0] || ''}
  </div>
)}

                <div className='min-w-0'>
                  <p className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {user.firstName} {user.lastName}
                  </p>
                  <p className={`text-xs truncate ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>
                    {user.email}
                  </p>
                </div>
              </div>
              <button onClick={() => handleEdit(user)} className={`p-1 rounded-full ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                <Edit size={16} />
              </button>
            </div>
            <div className={`grid grid-cols-2 gap-y-3 text-sm pt-3 border-t ${darkMode ? 'text-white/80 border-white/10' : 'text-gray-700 border-gray-100'}`}>
             <div className="break-all">
  Phone<br />{user.phoneNumber}
</div>
              <div>Role<br />{user.role}</div>
              <div>Dept.<br />{user.department}</div>
              <div>Position<br />{user.position}</div>
              <div>Location<br />{user.location}</div>
              <div>Created<br />{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4 pt-0 mt-0">

      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>User Management</h3>
        {!isMobileView && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 text-white">
              <Plus size={18} /> Add User
            </button>
          </div>
        )}
      </div>

      {isMobileView && renderMobileControls()}

      {isMobileView ? (
        renderUserCards()
      ) : (
        <UserTable
          users={mappedMobileUsers}
          onDeleteSelected={(ids) => requestDeleteSelected(ids)}
          onEdit={handleEdit}
          darkMode={darkMode}
        />
      )}

      <AddUserModal open={showAdd} onClose={() => setShowAdd(false)} backendUrl={process.env.REACT_APP_BACKEND_URL || 'https://vela-back.wingtrill.com'} onOpenBulk={() => { setShowAdd(false); setShowBulk(true); }} darkMode={darkMode} />
      <BulkUploadModal open={showBulk} onClose={() => setShowBulk(false)} onSubmit={onSubmitBulk} darkMode={darkMode} />
      <EditUserModal open={!!editingUser} user={editingUser} onClose={() => setEditingUser(null)} backendUrl={process.env.REACT_APP_BACKEND_URL || 'https://vela-back.wingtrill.com'} onChangePassword={handleChangePassword} darkMode={darkMode} />

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className={`${darkMode ? 'bg-[#232336] text-white' : 'bg-white'} rounded-2xl w-full max-w-md p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Confirm Deletion</h3>
              <button onClick={() => setConfirmOpen(false)}><X /></button>
            </div>
            <div className="space-y-4">
              <div>You are about to delete {confirmIds.length} users.</div>
              <input value={confirmInput} onChange={e => setConfirmInput(e.target.value)} placeholder="DELETE" className={`w-full rounded-lg px-3 py-2 border ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : ''}`} />
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 rounded-lg border">Cancel</button>
                <button onClick={confirmDelete} disabled={confirmLoading || confirmInput !== 'DELETE'} className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 text-white">
                  {confirmLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
